// src/employee/pages/HrAssignTasks.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection, query, orderBy, onSnapshot, updateDoc, doc, getDocs, serverTimestamp, where
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch, FaCalendarAlt, FaCheckCircle, FaUserCircle, FaBuilding,
  FaFileAlt, FaStar, FaChevronRight, FaFilter, FaPaperPlane
} from "react-icons/fa";
import { format } from "date-fns";

const HrAssignTasks = () => {
  const { user: hr } = useEmployeeAuth();
  const [employees, setEmployees] = useState([]);
  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterDate, setFilterDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [showAllDates, setShowAllDates] = useState(false);

  // Review State
  const [selectedDiary, setSelectedDiary] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Load employees
  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(query(collection(db, "employees"), orderBy("name")));
      setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    load();
  }, []);

  // Realtime Diaries
  useEffect(() => {
    let q = query(collection(db, "dailyDiaries"), orderBy("updatedAt", "desc"));

    // If we only want today's or a specific date, we can filter in memory or via query
    // Firestore composite queries require indexes, so I'll do some filtering in useMemo 
    // but the initial stream pulls recent diaries.

    const unsub = onSnapshot(q, snap => {
      setDiaries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filteredDiaries = useMemo(() => {
    return diaries.filter(d => {
      const empMatch = !filterEmployee || d.employeeEmail === filterEmployee;
      const deptMatch = !filterDept || d.department === filterDept;
      const dateMatch = showAllDates || !filterDate || d.date === filterDate;
      return empMatch && deptMatch && dateMatch;
    });
  }, [diaries, filterEmployee, filterDept, filterDate, showAllDates]);

  const handleReview = async (e) => {
    e.preventDefault();
    if (!selectedDiary) return;
    setSavingFeedback(true);
    try {
      await updateDoc(doc(db, "dailyDiaries", selectedDiary.id), {
        hrFeedback: feedback,
        hrRating: rating,
        hrReviewedAt: serverTimestamp(),
        hrReviewer: hr.email
      });
      setSelectedDiary(null);
      setFeedback("");
      setRating(0);
      alert("Review submitted!");
    } catch (err) {
      console.error(err);
      alert("Failed to submit review");
    } finally {
      setSavingFeedback(false);
    }
  };

  const departments = useMemo(() => {
    return [...new Set(employees.map(e => e.department))].filter(Boolean);
  }, [employees]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center space-y-2 pt-10">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-6xl font-black bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent"
          >
            Employee Diary Hub
          </motion.h1>
          <p className="text-white/40 text-lg">Review daily logs, track progress, and provide mentorship.</p>
        </div>

        {/* Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-wrap gap-4 items-center justify-between shadow-2xl"
        >
          <div className="flex flex-wrap gap-3 flex-1">
            <div className="relative flex-1 min-w-[200px]">
              <FaUserCircle className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <select
                value={filterEmployee}
                onChange={e => setFilterEmployee(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white/5 border border-white/10 rounded-2xl appearance-none outline-none focus:ring-2 focus:ring-cyan-500 transition"
              >
                <option value="" className="bg-neutral-900">All Employees</option>
                {employees.map(e => (
                  <option key={e.email} value={e.email} className="bg-neutral-900">{e.name}</option>
                ))}
              </select>
            </div>

            <div className="relative flex-1 min-w-[200px]">
              <FaBuilding className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <select
                value={filterDept}
                onChange={e => setFilterDept(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white/5 border border-white/10 rounded-2xl appearance-none outline-none focus:ring-2 focus:ring-purple-500 transition"
              >
                <option value="" className="bg-neutral-900">All Departments</option>
                {departments.map(d => (
                  <option key={d} value={d} className="bg-neutral-900">{d}</option>
                ))}
              </select>
            </div>

            <div className={`relative flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 transition ${showAllDates ? 'opacity-50' : ''}`}>
              <FaCalendarAlt className="text-white/30" />
              <input
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                disabled={showAllDates}
                className="bg-transparent outline-none cursor-pointer"
              />
            </div>
          </div>

          <button
            onClick={() => setShowAllDates(!showAllDates)}
            className={`px-6 py-3 rounded-2xl font-bold transition flex items-center gap-2 ${showAllDates ? 'bg-cyan-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
          >
            <FaFilter /> {showAllDates ? "Filtered by Date" : "Show All History"}
          </button>
        </motion.div>

        {/* Diary List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full py-20 text-center text-white/20 animate-pulse">Scanning diaries...</div>
          ) : filteredDiaries.length === 0 ? (
            <div className="col-span-full py-20 text-center text-white/20">No diary entries found for this selection.</div>
          ) : (
            filteredDiaries.map((diary, i) => {
              const emp = employees.find(e => e.email === diary.employeeEmail);
              return (
                <motion.div
                  key={diary.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="group relative backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-all shadow-xl flex flex-col h-full"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={`https://ui-avatars.com/api/?name=${diary.employeeName}&background=0f172a&color=06b6d4&bold=true`}
                        alt=""
                        className="w-10 h-10 rounded-full border border-white/20"
                      />
                      <div>
                        <h3 className="font-bold text-lg leading-tight">{diary.employeeName}</h3>
                        <p className="text-[10px] text-cyan-400 uppercase tracking-widest">{diary.date}</p>
                      </div>
                    </div>
                    {diary.hrFeedback && (
                      <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-black px-2 py-1 rounded-full uppercase">Reviewed</span>
                    )}
                  </div>

                  <div className="flex-1 space-y-4">
                    <div>
                      <p className="text-white/60 text-xs line-clamp-3 italic">
                        {diary.notes || "No notes provided."}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="bg-white/5 border border-white/10 px-2 py-1 rounded text-white/40">
                        {diary.tasks?.length || 0} Tasks
                      </span>
                      <span className="bg-white/5 border border-white/10 px-2 py-1 rounded text-white/40">
                        {diary.attachments?.length || 0} Files
                      </span>
                      <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                        {diary.department}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setSelectedDiary(diary);
                        setFeedback(diary.hrFeedback || "");
                        setRating(diary.hrRating || 0);
                      }}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 rounded-2xl font-bold text-sm shadow-lg group-hover:scale-[1.02] active:scale-95 transition flex items-center justify-center gap-2"
                    >
                      View Details & Review <FaChevronRight size={12} />
                    </button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Review Modal */}
        <AnimatePresence>
          {selectedDiary && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedDiary(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-neutral-900 border border-white/20 rounded-[40px] w-full max-w-4xl p-10 max-h-[90vh] overflow-y-auto custom-scrollbar"
                onClick={e => e.stopPropagation()}
              >
                <div className="grid md:grid-cols-2 gap-10">

                  {/* Diary Content */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <img
                        src={`https://ui-avatars.com/api/?name=${selectedDiary.employeeName}&background=0f172a&color=06b6d4&bold=true`}
                        alt=""
                        className="w-16 h-16 rounded-full border-2 border-cyan-500/50"
                      />
                      <div>
                        <h2 className="text-3xl font-black text-white">{selectedDiary.employeeName}</h2>
                        <p className="text-cyan-400 font-bold tracking-widest uppercase text-sm">{selectedDiary.date}</p>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                      <h4 className="text-xs font-black text-white/30 uppercase mb-3 flex items-center gap-2">
                        <FaFileAlt /> Daily Notes
                      </h4>
                      <p className="text-white/80 whitespace-pre-wrap text-sm leading-relaxed">
                        {selectedDiary.notes || "No notes provided today."}
                      </p>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-xs font-black text-white/30 uppercase flex items-center gap-2">
                        <FaCheckCircle /> Checklist Progress
                      </h4>
                      <div className="space-y-2">
                        {selectedDiary.tasks?.map((t, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-white/5 p-3 rounded-2xl text-xs">
                            {t.completed ? <FaCheckCircle className="text-green-400" /> : <div className="w-4 h-4 rounded-full border border-white/20" />}
                            <span className={t.completed ? "text-white/40 line-through" : "text-white/80"}>{t.text}</span>
                          </div>
                        ))}
                        {(!selectedDiary.tasks || selectedDiary.tasks.length === 0) && <p className="text-white/20 text-xs italic">No specific tasks listed.</p>}
                      </div>
                    </div>

                    {selectedDiary.attachments?.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-black text-white/30 uppercase">Attachments</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {selectedDiary.attachments.map((file, i) => (
                            <a key={i} href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-cyan-500/10 p-3 rounded-2xl border border-cyan-500/20 text-xs text-cyan-400 hover:bg-cyan-500/20 transition">
                              <FaFileAlt /> <span className="truncate">{file.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Review Sidebar */}
                  <div className="bg-white/5 rounded-3xl p-8 space-y-8 flex flex-col border border-white/10">
                    <h3 className="text-2xl font-bold flex items-center gap-3 text-cyan-400">
                      <FaPaperPlane /> Write Review
                    </h3>

                    <div className="space-y-4">
                      <label className="text-xs font-black text-white/30 uppercase">Performance Rating</label>
                      <div className="flex gap-4">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            onClick={() => setRating(star)}
                            className={`text-3xl transition ${star <= rating ? 'text-yellow-400 scale-110' : 'text-white/10 hover:text-white/20 hover:scale-105'}`}
                          >
                            <FaStar />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex-1 space-y-4">
                      <label className="text-xs font-black text-white/30 uppercase">Feedback / Guidance</label>
                      <textarea
                        className="w-full h-48 bg-neutral-950 border border-white/10 rounded-2xl p-5 text-sm text-white placeholder-white/20 focus:ring-2 focus:ring-cyan-500 outline-none resize-none transition"
                        placeholder="Write your feedback here... Encouragement, corrections, or questions."
                        value={feedback}
                        onChange={e => setFeedback(e.target.value)}
                      />
                    </div>

                    <div className="space-y-4 pt-4">
                      <button
                        onClick={handleReview}
                        disabled={savingFeedback}
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:scale-105 active:scale-95 transition disabled:opacity-50"
                      >
                        {savingFeedback ? "Submitting..." : "Submit Review"}
                      </button>
                      <button
                        onClick={() => setSelectedDiary(null)}
                        className="w-full py-4 text-white/40 hover:text-white transition font-bold"
                      >
                        Maybe Later
                      </button>
                    </div>
                  </div>

                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <div className="text-center py-10 opacity-20 text-xs">
          HR Management Console v4.0 • Built for Performance
        </div>

      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default HrAssignTasks;
